/**
 * sentryLazy — carga diferida de @sentry/react (P-3, audit 2026-07-06).
 *
 * @sentry/react pesaba en el main chunk (~90KB gz) y bloqueaba el first paint.
 * Este helper lo carga con dynamic import después del primer render:
 *   - loadSentry(init): dispara la carga y ejecuta init(S) al llegar el módulo.
 *   - withSentry(fn):   ejecuta fn(S) inmediato si ya cargó, o la encola.
 *
 * Las llamadas encoladas (setUser, captureException) se ejecutan en orden al
 * completar la carga — se pierde como máximo ~1s de telemetría inicial, nunca
 * errores capturados después de la carga.
 *
 * NOTA: SupabaseWriteService.ts (Triple Write — protegido) mantiene su import
 * estático; Rollup lo resuelve al mismo chunk async compartido, no al main.
 */
import type * as SentryNS from '@sentry/react';

export type SentryModule = typeof SentryNS;

let mod: SentryModule | null = null;
const queue: Array<(S: SentryModule) => void> = [];

/** Dispara la carga del SDK y corre `init` (Sentry.init + config) al llegar. */
export function loadSentry(init: (S: SentryModule) => void): void {
  void import('@sentry/react').then((S) => {
    mod = S;
    init(S);
    queue.splice(0).forEach((fn) => fn(S));
  });
}

/** Ejecuta `fn` con el SDK — inmediato si cargó, encolada si aún no. */
export function withSentry(fn: (S: SentryModule) => void): void {
  if (mod) {
    fn(mod);
  } else {
    queue.push(fn);
  }
}
